import type { IObservableArray } from 'mobx';
import { observable, runInAction } from 'mobx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, ITargetable, Targeting } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { useLocationFilters } from '../../Targetings.useLocationFilters';
import { extractGeoFilterIdsFromTargetings } from '../../Targetings.utils';
import type { TGeoFilterModalContext } from '../Locations.useModals';
import { useModals } from '../Locations.useModals';
import { computeIsDirty, validateGeoFilterSetName } from './GeoFilterModal.utils';

/**
 * Data structure for a geofilter set, used when creating or editing sets.
 */
export interface IGeoFilterSetData extends Pick<ITargetable, 'name'> {
  is_default?: boolean;
  included_geo_filters: string[];
  excluded_geo_filters: string[];
}

/**
 * Parameters for the useGeoFilterModalForm hook.
 */
interface IUseGeoFilterModalParams {
  /** Whether the modal is currently open */
  isModalOpen: boolean;
  /** Initial data to populate the form when editing an existing geofilter set */
  initialData?: IGeoFilterSetData;
  /** The campaign associated with this geofilter set */
  campaign?: Campaign;
  /** The modal context - 'create' for new sets, 'edit' for existing */
  context: TGeoFilterModalContext;
  /** Callback invoked when the user saves changes */
  onSaveChanges?: (data: IGeoFilterSetData) => Promise<void>;
  /** Callback to close the modal */
  closeModal: () => void;
}

/**
 * Return type for the useGeoFilterModalForm hook.
 */
interface IUseGeoFilterModalReturn {
  /** Current geofilter set name */
  geoFilterSetName: string;
  /** Setter for the geofilter set name */
  setGeoFilterSetName: (name: string) => void;
  /** Whether this set is marked as the default */
  isDefault: boolean;
  /** Setter for the default toggle */
  setIsDefault: (value: boolean) => void;
  /** Whether a save operation is in progress */
  isSaving: boolean;
  /** Whether the form has unsaved changes compared to initialData */
  isDirty: boolean;
  /** Observable array of targetings managed by this modal (not persisted until save) */
  ephemeralTargetings: IObservableArray<Targeting>;
  /** Modal state controller for the save changes confirmation dialog */
  saveChanges: ReturnType<typeof useModals>['saveChanges'];
  /** Handler for the primary action button (Save in create mode, opens confirmation in edit mode) */
  handleApply: () => Promise<void>;
  /** Handler for confirming save in edit mode (called from SaveChangesModal) */
  handleSaveChangesSubmit: () => Promise<void>;
  /** Filtered list of targetings (excludes invalid/incomplete ones for display) */
  filteredTargetings: Targeting[];
  /** Number of remaining location slots available */
  remainingLocations: number;
  /** Whether any targetings are incomplete/invalid */
  hasInvalidTargetings: boolean;
  /** Validation errors preventing adding a new filter */
  addFilterErrors: string[];
  /** Handler to add a new location filter */
  handleAddFilter: () => void;
  /** Handler to remove a location filter */
  handleRemoveFilter: (targeting: Targeting) => void;
  /** Validation errors for the name field (for inline display) */
  nameValidationErrors: string[];
}

/**
 * Custom hook that manages all state and logic for the GeoFilterModal.
 *
 * This hook encapsulates:
 * - Form state (name, isDefault, saving status)
 * - Ephemeral targetings that are only persisted on save
 * - Synchronization of initial data when the modal opens
 * - Save/apply handlers with error handling
 * - Integration with useLocationFilters for filter management
 *
 * @param params - Configuration parameters for the hook
 * @returns Object containing all state values, setters, and handlers needed by the modal
 *
 * @example
 * ```tsx
 * const {
 *   geoFilterSetName,
 *   setGeoFilterSetName,
 *   handleApply,
 *   filteredTargetings,
 *   // ...other values
 * } = useGeoFilterModalForm({
 *   isModalOpen: true,
 *   context: 'create',
 *   closeModal: () => setOpen(false),
 *   onSaveChanges: async (data) => {
 *     await api.createGeoFilterSet(data);
 *   },
 * });
 * ```
 */
export function useGeoFilterModalForm({
  isModalOpen,
  initialData,
  campaign,
  context,
  onSaveChanges,
  closeModal,
}: IUseGeoFilterModalParams): IUseGeoFilterModalReturn {
  const { t } = useTranslation();
  const { saveChanges } = useModals();
  const { Targetables } = useStore();

  const [geoFilterSetName, setGeoFilterSetName] = useState(initialData?.name ?? '');
  const [isDefault, setIsDefault] = useState(initialData?.is_default ?? false);
  const [isSaving, setIsSaving] = useState(false);
  const [ephemeralTargetings] = useState<IObservableArray<Targeting>>(() => observable.array([]));

  const {
    filteredTargetings,
    remainingLocations,
    hasInvalidTargetings,
    addFilterErrors,
    handleAddFilter,
    handleRemoveFilter,
  } = useLocationFilters({
    campaign,
    targetings: ephemeralTargetings,
  });

  useSyncInitialData({
    isModalOpen,
    initialData,
    campaign,
    ephemeralTargetings,
    setGeoFilterSetName,
    setIsDefault,
  });

  const isDirty = computeIsDirty({
    geoFilterSetName,
    isDefault,
    initialData,
    filteredTargetings,
    Targetables,
  });

  const nameValidationErrors = validateGeoFilterSetName(geoFilterSetName, context, t);

  /**
   * Builds the geofilter set data object from current form state.
   * Extracts included/excluded filter IDs from the ephemeral targetings.
   */
  const buildGeoFilterSetData = (): IGeoFilterSetData => {
    const { included, excluded } = extractGeoFilterIdsFromTargetings(
      filteredTargetings,
      Targetables,
    );

    return {
      name: geoFilterSetName,
      is_default: isDefault,
      included_geo_filters: included,
      excluded_geo_filters: excluded,
    };
  };

  /**
   * Handles the primary action button click.
   * In 'create' mode: saves immediately and shows toast on error.
   * In 'edit' mode: opens the save changes confirmation modal.
   */
  const handleApply = async (): Promise<void> => {
    if (context === 'create') {
      if (!onSaveChanges) {
        closeModal();
        return;
      }

      setIsSaving(true);
      try {
        await onSaveChanges(buildGeoFilterSetData());
      } catch (error) {
        showError(error, t);
        toast(t('Failed to save geofilter set. Please try again.'), {
          type: 'error',
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      saveChanges.open();
    }
  };

  /**
   * Handles the confirmation from SaveChangesModal in edit mode.
   * Saves the data and closes both the confirmation and main modal on success.
   */
  const handleSaveChangesSubmit = async (): Promise<void> => {
    if (!onSaveChanges) {
      return;
    }

    setIsSaving(true);
    try {
      await onSaveChanges(buildGeoFilterSetData());
      saveChanges.close();
      closeModal();
    } catch (error) {
      showError(error, t);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    geoFilterSetName,
    setGeoFilterSetName,
    isDefault,
    setIsDefault,
    isSaving,
    isDirty,
    ephemeralTargetings,
    saveChanges,
    handleApply,
    handleSaveChangesSubmit,
    filteredTargetings,
    remainingLocations,
    hasInvalidTargetings,
    addFilterErrors,
    handleAddFilter,
    handleRemoveFilter,
    nameValidationErrors,
  };
}

/**
 * Parameters for the useSyncInitialData hook.
 */
interface IUseSyncInitialDataParams {
  /** Whether the modal is currently open */
  isModalOpen: boolean;
  /** Initial data to populate when editing */
  initialData?: IGeoFilterSetData;
  /** Campaign associated with the geofilter set */
  campaign?: Campaign;
  /** Observable array to populate with targetings */
  ephemeralTargetings: IObservableArray<Targeting>;
  /** Setter for the geofilter set name */
  setGeoFilterSetName: (name: string) => void;
  /** Setter for the default toggle */
  setIsDefault: (value: boolean) => void;
}

/**
 * Internal hook that synchronizes initial data into the form state when the modal opens.
 *
 * When the modal opens with initialData:
 * 1. Sets the name and isDefault values from initialData
 * 2. Clears any existing ephemeral targetings
 * 3. Creates new Targeting objects for each geofilter ID in initialData
 * 4. Populates the ephemeralTargetings array with the created targetings
 *
 * This allows editing an existing geofilter set while keeping changes
 * ephemeral until the user explicitly saves.
 *
 * @param params - Configuration parameters
 */
function useSyncInitialData({
  isModalOpen,
  initialData,
  campaign,
  ephemeralTargetings,
  setGeoFilterSetName,
  setIsDefault,
}: IUseSyncInitialDataParams): void {
  const { GeoFilters, Targetables, Targetings } = useStore();
  const wasModalOpenRef = useRef(false);

  useEffect(() => {
    const isOpening = isModalOpen && !wasModalOpenRef.current;
    wasModalOpenRef.current = isModalOpen;

    if (!isOpening) {
      return;
    }

    if (!initialData) {
      setGeoFilterSetName('');
      setIsDefault(false);
      runInAction(() => {
        ephemeralTargetings.clear();
      });
      return;
    }

    setGeoFilterSetName(initialData.name);
    setIsDefault(initialData.is_default ?? false);

    runInAction(() => {
      ephemeralTargetings.clear();

      const allGeoFilterIds = [
        ...initialData.included_geo_filters,
        ...initialData.excluded_geo_filters,
      ];

      allGeoFilterIds.forEach((geoFilterId) => {
        const geoFilter = GeoFilters.get(geoFilterId);
        if (!geoFilter) {
          return;
        }

        const isIncluded = initialData.included_geo_filters.includes(geoFilterId);

        const targetable = Targetables.create({
          _cls: TargetableClass.geo,
          name: geoFilter.get('name'),
          geo_filter: geoFilterId,
        });

        const targeting = Targetings.create({
          parent: campaign?.get('id'),
          kind: 'geo',
          included: isIncluded,
          target_data: targetable.id,
          name: geoFilter.get('name'),
        });

        ephemeralTargetings.push(targeting);
      });
    });
  }, [
    isModalOpen,
    initialData,
    campaign,
    GeoFilters,
    Targetables,
    Targetings,
    ephemeralTargetings,
    setGeoFilterSetName,
    setIsDefault,
  ]);
}
